import React, { useState, useRef, useCallback, useMemo } from 'react'
import { Icon } from '@iconify/react'
import DefaultProfile from '../../../assets/images/profile/default-profile.png'
import { createPost } from '../../../services/user/feed.services'
import { toast } from '../../../components/ui/notify'
import BitmovinPlayer from '../../../components/common/BitmovinPlayer'

// Move MediaUploadButton outside to prevent recreation on every render
const MediaUploadButton = React.memo(({ icon, text, onClick, buttonStyle }) => (
  <button className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] !tw-text-[var(--secondary-color)] [&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-font-medium [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button tw-border tw-text-slate-500 ui-button-sm"} style={buttonStyle} onClick={onClick} type="button">
    <Icon icon={icon} className="tw-me-1 tw-hidden md:tw-inline" />
    <Icon icon={icon} className="md:tw-hidden" />
    <span className="tw-hidden md:tw-inline">{text}</span>
  </button>
));

// Match backend feed route limit (50MB)
const MAX_MEDIA_SIZE_BYTES = 50 * 1024 * 1024;
const MAX_MEDIA_SIZE_MB = 50;

const FeedPost = ({ onPostSubmit, userProfile }) => {
  const [newPost, setNewPost] = useState('');
  const [newPostMedia, setNewPostMedia] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const MAX_CHARACTERS = 1000;

  // Refs for file inputs
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Memoized styles to prevent re-renders
  const buttonStyle = useMemo(() => ({
    backgroundColor: 'transparent',
    borderColor: '#dee2e6'
  }), []);

  const postButtonStyle = useMemo(() => ({
    borderRadius: '20px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    minWidth: '100px'
  }), []);

  // Event handlers
  const handleMediaUpload = useCallback((e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_MEDIA_SIZE_BYTES) {
      toast.error(`File is too large. Please choose an image or video under ${MAX_MEDIA_SIZE_MB}MB.`);
      e.target.value = '';
      return;
    }
    setNewPostMedia({
      type,
      url: URL.createObjectURL(file),
      file
    });
  }, []);

  const handleImageUpload = useCallback((e) => {
    handleMediaUpload(e, 'image');
  }, [handleMediaUpload]);

  const handleVideoUpload = useCallback((e) => {
    handleMediaUpload(e, 'video');
  }, [handleMediaUpload]);

  const handleImageButtonClick = useCallback(() => {
    imageInputRef.current?.click();
  }, []);

  const handleVideoButtonClick = useCallback(() => {
    videoInputRef.current?.click();
  }, []);

  // Memoized textarea change handler
  const handleTextareaChange = useCallback((e) => {
    setNewPost(e.target.value);
  }, []);

  // Memoized computed values
  const hasContent = useMemo(() => newPost.trim() || newPostMedia, [newPost, newPostMedia]);
  const isPostButtonEnabled = useMemo(() => hasContent && !isSubmitting, [hasContent, isSubmitting]);
  const postButtonClass = useMemo(() =>
    `[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] ui-button btn px-4 w-auto py-2 ${isPostButtonEnabled ? "[&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button-primary" : "[--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button-secondary"}`,
    [isPostButtonEnabled]
  );

  const handleSubmitPost = async () => {
    if (!newPost.trim() && !newPostMedia) {
      toast.error('Please add some content or media to your post');
      return;
    }

    if (newPost.length > MAX_CHARACTERS) {
      toast.error(`Post content cannot exceed ${MAX_CHARACTERS} characters`);
      return;
    }

    setIsSubmitting(true);
    try {
      const postData = {
        description: newPost.trim(),
        media: newPostMedia
      };

      const response = await createPost(postData);

      if (response.success) {
        toast.success('Post created successfully');
        setNewPost('');
        setNewPostMedia(null);

        // Call the parent callback to refresh the feed (backend returns { post_id, share_url })
        if (onPostSubmit) {
          onPostSubmit(response.data);
        }
      } else {
        toast.error(response.message || response.error?.message || 'Failed to create post');
      }
    } catch (error) {
      const message = error?.message || error?.response?.data?.message || 'Error creating post';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render functions
  const renderMedia = (media) => {
    if (media.type === 'image') {
      return (
        <div className="tw-relative" style={{ width: '100%', maxHeight: '400px', overflow: 'hidden', borderRadius: '8px' }}>
          <img
            src={media.url}
            className="tw-max-w-full"
            alt="Post media"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '400px',
              display: 'block',
              objectFit: 'contain'
            }}
          />
        </div>
      );
    } else if (media.type === 'video') {
      return (
        <div className="tw-relative tw-rounded-xl tw-overflow-hidden" style={{ width: '100%', background: '#000' }}>
          <BitmovinPlayer
            src={media.url}
            title="Post video"
            objectFit="contain"
            style={{ maxHeight: '400px', width: '100%' }}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card tw-mb-6"}>
      <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
        <div className="tw-flex tw-mb-4">
          <img
            src={userProfile?.profile_pic_url || DefaultProfile}
            className="tw-rounded-full tw-me-4"
            alt={userProfile?.name || "Profile"}
            style={{width: '40px', height: '40px'}}
          />
          <div className="tw-grow">
            <textarea
              className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-border-0"}
              rows="3"
              placeholder="What's on your mind?"
              value={newPost}
              onChange={handleTextareaChange}
              maxLength={MAX_CHARACTERS}
            />
            <div className="tw-flex tw-justify-end tw-mt-2">
              <small className={newPost.length > MAX_CHARACTERS * 0.9 ? "!tw-text-[var(--warning-color)] tw-text-amber-600" : "!tw-text-[var(--secondary-color)] tw-text-slate-500"}>
                {newPost.length}/{MAX_CHARACTERS} characters
              </small>
            </div>
          </div>
        </div>

        {/* Media Preview */}
        {newPostMedia && (
          <div className="tw-mb-4">
            <div className="tw-relative">
              {renderMedia(newPostMedia)}
              <button
                className="tw-absolute tw-right-2 tw-top-2 tw-z-10 tw-inline-flex tw-size-9 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-white/80 tw-bg-white tw-p-0 tw-text-rose-600 tw-shadow-md tw-transition-colors hover:tw-bg-rose-50 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-rose-600"
                type="button"
                onClick={() => setNewPostMedia(null)}
                aria-label="Remove attachment"
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
          </div>
        )}

        {/* Hidden file inputs */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="tw-hidden"
          onChange={handleImageUpload}
        />
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          className="tw-hidden"
          onChange={handleVideoUpload}
        />

        {/* Action Buttons */}
        <div className="tw-flex tw-justify-between tw-items-center">
          <div className="tw-flex tw-gap-2">
            <MediaUploadButton
              icon="mdi:camera"
              text="Photo"
              onClick={handleImageButtonClick}
              buttonStyle={buttonStyle}
            />
            <MediaUploadButton
              icon="mdi:video"
              text="Video"
              onClick={handleVideoButtonClick}
              buttonStyle={buttonStyle}
            />
          </div>
          <button
            className={postButtonClass}
            style={postButtonStyle}
            onClick={handleSubmitPost}
            disabled={!isPostButtonEnabled}
          >
            {isSubmitting ? (
              <>
                <div className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" role="status">
                  <span className="tw-sr-only">Loading...</span>
                </div>
                Posting...
              </>
            ) : (
              <>
                <Icon icon="mdi:send" className="tw-me-2" />
                Post
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedPost;
